import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import ImageUploader from "../../components/ImageUploader";

const slugify = (t) => (t || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const EMPTY = { label: "", image_url: "", visible: true, order: 0, parent_id: "", subcategories: [] };

export default function AdminCategories({ onChanged }) {
  const [cats, setCats] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [subInput, setSubInput] = useState("");

  const load = () => api.get("/categories?all=true").then((r) => setCats(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const refreshAll = () => { load(); onChanged?.(); };

  const openNew = () => { setEditing(null); setForm(EMPTY); setSubInput(""); setShowForm(true); };
  const openEdit = (c) => { setEditing(c.id); setForm({ ...c, subcategories: c.subcategories || [] }); setSubInput(""); setShowForm(true); };

  const toggleVisible = async (c) => {
    await api.put(`/categories/${c.id}`, { ...c, visible: !c.visible });
    toast.success(c.visible ? "Category hidden" : "Category shown");
    refreshAll();
  };

  const addSub = () => {
    if (!subInput.trim()) return;
    const key = slugify(subInput);
    if (form.subcategories.some((s) => s.key === key)) return;
    setForm({ ...form, subcategories: [...form.subcategories, { key, label: subInput.trim() }] });
    setSubInput("");
  };
  const removeSub = (key) => setForm({ ...form, subcategories: form.subcategories.filter((s) => s.key !== key) });

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      label: form.label,
      image_url: form.image_url,
      visible: form.visible,
      order: parseInt(form.order) || 0,
      parent_id: form.parent_id || null,
      subcategories: form.subcategories,
    };
    try {
      if (editing) await api.put(`/categories/${editing}`, payload);
      else await api.post("/categories", payload);
      toast.success(editing ? "Category updated" : "Category added");
      setShowForm(false);
      refreshAll();
    } catch {
      toast.error("Could not save category");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this category? Products in it will remain but become uncategorised.")) return;
    await api.delete(`/categories/${id}`);
    toast.success("Category deleted");
    refreshAll();
  };

  return (
    <div className="mt-6">
      <button data-testid="admin-add-category" onClick={openNew} className="inline-flex items-center gap-2 bg-[var(--brand-primary)] text-white px-4 py-2.5 rounded-lg font-medium hover:bg-[var(--brand-primary-hover)] transition-colors mb-4">
        <Plus className="w-4 h-4" /> Add Category
      </button>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cats.map((c) => (
          <div key={c.id} data-testid={`admin-category-${c.key}`} className={`bg-white border border-[#E8E4D9] rounded-xl p-4 ${c.visible ? "" : "opacity-60"}`}>
            <div className="flex gap-3">
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#F4EFE6] shrink-0">
                {c.image_url && <img src={c.image_url} alt={c.label} className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display font-medium text-[#1A231C] truncate">{c.label}</p>
                <p className="text-xs text-[#5C665D]">Level {(c.depth || 0) + 1} · {(c.subcategories || []).length} tags</p>
                <div className="flex gap-1 mt-2">
                  <button data-testid={`category-visible-${c.key}`} onClick={() => toggleVisible(c)} title={c.visible ? "Hide" : "Show"} className="text-[var(--brand-primary)] p-1.5 rounded hover:bg-[#F4EFE6]">
                    {c.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button data-testid={`category-edit-${c.key}`} onClick={() => openEdit(c)} className="text-[var(--brand-primary)] p-1.5 rounded hover:bg-[#F4EFE6]">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button data-testid={`category-delete-${c.key}`} onClick={() => del(c.id)} className="text-[var(--brand-accent)] p-1.5 rounded hover:bg-[#F4EFE6]">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold text-lg text-[#1A231C]">{editing ? "Edit Category" : "Add Category"}</h3>
              <button type="button" onClick={() => setShowForm(false)}><X className="w-5 h-5 text-[#5C665D]" /></button>
            </div>
            <div className="space-y-3">
              <input data-testid="category-form-label" required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Category name" className="w-full bg-white border border-[#E8E4D9] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--brand-primary)]" />
              <div>
                <label className="text-xs text-[#5C665D]">Parent category (leave empty for top level)</label>
                <select data-testid="category-form-parent" value={form.parent_id || ""} onChange={(e) => setForm({ ...form, parent_id: e.target.value })} className="w-full mt-1 bg-white border border-[#E8E4D9] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--brand-primary)]">
                  <option value="">— Top level —</option>
                  {cats.filter((c) => c.id !== editing && (c.depth || 0) < 3).map((c) => (
                    <option key={c.id} value={c.id}>{"\u00A0".repeat((c.depth || 0) * 3)}{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3 items-center">
                <input data-testid="category-form-order" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} placeholder="Order" className="w-full bg-white border border-[#E8E4D9] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--brand-primary)]" />
                <label className="flex items-center gap-2 text-sm text-[#1A231C]">
                  <input data-testid="category-form-visible" type="checkbox" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} /> Visible on site
                </label>
              </div>
              <ImageUploader label="Category Image" testid="category-image" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} />

              <div>
                <label className="text-sm font-medium text-[#1A231C]">Sub-categories</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.subcategories.map((s) => (
                    <span key={s.key} className="inline-flex items-center gap-1 bg-[#F4EFE6] text-[#1A231C] text-sm px-2.5 py-1 rounded-full">
                      {s.label}
                      <button type="button" onClick={() => removeSub(s.key)}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <input data-testid="subcat-input" value={subInput} onChange={(e) => setSubInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSub(); } }} placeholder="Add sub-category" className="flex-1 bg-white border border-[#E8E4D9] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[var(--brand-primary)]" />
                  <button type="button" data-testid="subcat-add" onClick={addSub} className="bg-[#F4EFE6] text-[#1A231C] px-3 rounded-lg hover:bg-[#E8E4D9]">Add</button>
                </div>
              </div>
            </div>
            <button data-testid="category-form-save" type="submit" className="w-full mt-5 bg-[var(--brand-primary)] text-white px-6 py-3 rounded-lg font-medium hover:bg-[var(--brand-primary-hover)] transition-colors">
              {editing ? "Update" : "Add"} Category
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
